var _ = require('underscore');

var ModalDialogWidget = require('./../widget/modal/ModalDialogWidget');
var billingConfigModel = require('./../financial/billingConfigModel');
var PaymentTransactionModel = require('./PaymentTransactionModel');
var DynamicClassNameBehavior = require('./../behaviors/DynamicClassNameBehavior');
var paymentDataDeviceService = require('./paymentDataDeviceService');
var ModalBehavior = require('./../widget/modal/ModalBehavior');
var DemographicsDataBehavior = require('./../demographics/DemographicsDataBehavior');
var RenderIfDisplayedBehavior = require('./../behaviors/RenderIfDisplayedBehavior');
var applicationUtils = require('./../applicationUtils');
var userService = require('./../user/userService');

var submitPaymentModalTemplate = require('./submitPaymentModalTemplate.hbs');
var PAYMENT_POLLING_WAIT_MS = 1000;
var SUCCESS_STATUS_CODE_200 = 200;

/**
 * View for Portal Make Payment - Submitting Payment Modal
 *
 * @class SubmitPaymentModalView
 */
module.exports = ModalDialogWidget.extend({
  template: submitPaymentModalTemplate,

  behaviors: _.extend({
    className: DynamicClassNameBehavior,
    demographics: {
      behavior: DemographicsDataBehavior,
      returnSingleResult: true,
      id: { property: '_patientVecnaId' }
    },
    renderIfDisplayed: RenderIfDisplayedBehavior
  }, ModalDialogWidget.prototype.behaviors),

  events: _.extend({
    'click [data-select-payment-button]': '_selectNewPaymentMethod',
    'click [data-process-payment]': '_startProcessPayment',
    'click [data-retry-submitting-payment]': '_startProcessPayment',
    'click [data-retry-tablet-device-payment]': '_startProcessPayment'
  }, ModalDialogWidget.prototype.events),

  feedback: [
    /**
     * When this._paymentDetailsFormModel has validation error,
     * Then disable the Submit Payment button
     * @property feedback[1] (Disable/Enable Submit Payment button) {When/Then Feedback}
     */
    {
      when: {
        listenTo: [
          {
            object: '_paymentDetailsFormModel',
            events: 'change'
          },
          {
            object: function() {
              return this.getBehavior('demographics');
            },
            events: 'change:fetchSuccess'
          }
        ],
        'on': ['render:complete']
      },
      then: function() {
        var errors = this._paymentDetailsFormModel.validate();
        errors = _.keys(errors);
        var noErrors = errors.length === 0;
        var cardNeedsEncryption = errors.length === 1 && _.contains(errors, 'cardData.encryptedCardInfo');

        var fetchDemographicsSuccess = this.getBehavior('demographics').get('fetchSuccess');
        var hasPatientVecnaId = this._patientVecnaId;

        if (( noErrors || cardNeedsEncryption ) && fetchDemographicsSuccess && hasPatientVecnaId) {
          return {
            removeClass: 'brec-btn--disabled',
            removeAttr: ['disabled', 'disabled']
          };
        } else {
          return {
            addClass: 'brec-btn--disabled',
            attr: ['disabled', 'disabled']
          };
        }
      },
      to: 'submit-payment'
    },
    /**
     * When this._paymentDetailsFormModel has validation error,
     * Then show the warning feedback box and set "aria-hidden" to "true".
     * @property feedback[2] (Show validation warning feedback box) {When/Then Feedback}
     */
    {
      when: {
        listenTo: [
          {
            object: '_paymentDetailsFormModel',
            events: 'change'
          }
        ],
        'on': ['render:complete']
      },
      then: function() {
        var errors = this._paymentDetailsFormModel.validate();
        errors = _.keys(errors);
        var noErrors = errors.length === 0;
        var cardNeedsEncryption = errors.length === 1 && _.contains(errors, 'cardData.encryptedCardInfo');

        if ( noErrors || cardNeedsEncryption) {
          return {
            addClass: 'brec-hidden',
            attr: ['aria-hidden', 'true']
          };
        } else {
          return {
            removeClass: 'brec-hidden',
            attr: ['aria-hidden', 'false']
          };
        }
      },
      to: 'submitPaymentValidationWarningBox'
    },
    /**
     * When this._paymentDetailsFormModel has validation error,
     * Then display the first error as a warning message.
     * @property feedback[3] (Submit Payment validation warning) {When/Then Feedback}
     */
    {
      when: {
        listenTo: [
          {
            object: '_paymentDetailsFormModel',
            events: 'change'
          }
        ],
        'on': ['render:complete']
      },
      then: function() {
        var errors = this._paymentDetailsFormModel.validate();
        errors = _.keys(errors);
        var noErrors = errors.length === 0;
        var cardNeedsEncryption = errors.length === 1 && _.contains(errors, 'cardData.encryptedCardInfo');
        if ( noErrors || cardNeedsEncryption) {
          return {
            text: ''
          };
        } else if (errors[0] === 'cardData.encryptedCardInfo') {
          return {
            text: $.i18n.prop('payment.validationWarning.' + errors[1]),
            attr: ['data-test-payment-validation-warning', errors[1]]
          };
        } else {
          return {
            text: $.i18n.prop('payment.validationWarning.' + errors[0]),
            attr: ['data-test-payment-validation-warning', errors[0]]
          };
        }
      },
      to: 'submitPaymentValidationWarning'
    }
  ],

  prepareFields: _.extend({}, ModalDialogWidget.prototype.prepareFields, {
    paymentDetailsFormModel: '_paymentDetailsFormModel',
    multipleBillsPaymentInProgress: function() {
      var billPaymentsInProgress = this.get('billPaymentsInProgress');
      if (_.isEmpty(billPaymentsInProgress)) {
        return null;
      }
      return billPaymentsInProgress.length > 1;
    },
    singleBillPaymentInProgress: function() {
      var billPaymentsInProgress = this.get('billPaymentsInProgress');
      if (_.isEmpty(billPaymentsInProgress)) {
        return null;
      }
      var currentToBePaidBill;
      if (billPaymentsInProgress && billPaymentsInProgress.length !== 1) {
        // multiple payments being made so don't show individual payment info.
        return null;
      } else if (billPaymentsInProgress) {
        currentToBePaidBill = billPaymentsInProgress[0];
      } else {
        // This should only happen on initialize before the first payment is started.
        currentToBePaidBill = this._paymentDetailsFormModel.getFirstToBePaidBillPaymentDetails();
      }
      var billMessageVecnaIdOrAccountNumber = this._paymentDetailsFormModel.getBillMessageVecnaIdOrAccountNumberFromIdentifier(currentToBePaidBill.identifier);
      var currentPayableBillMessage = this._billMessageCollection && this._billMessageCollection.getMessageByVecnaId(billMessageVecnaIdOrAccountNumber);
      var currentPayableEstimate = this._insuranceEstimationCollection && this._insuranceEstimationCollection.getByAccountNumber(billMessageVecnaIdOrAccountNumber);
      if (!currentPayableBillMessage && !currentPayableEstimate) {
        return null;
      }
      var accountNumber = currentPayableBillMessage != null ? currentPayableBillMessage.get('billDTO.accountNumber') : currentPayableEstimate.get('accountNumber');
      var type = currentPayableBillMessage != null ? currentPayableBillMessage.get('billDTO.externalBill.type') : 'INSURANCE_ESTIMATION';
      return {
        payment: currentToBePaidBill,
        accountNumber: accountNumber,
        type: type
      };
    },
    previouslyPaidBill: function() {
      var previouslyPaidBills = this.get('previouslyPaidBill');
      if (_.isEmpty(previouslyPaidBills)) {
        return null;
      }
      var previouslyPaidBill;
      if (previouslyPaidBills.length === 1) {
        previouslyPaidBill = previouslyPaidBills[0];
      } else {
        // multiple payments being made so don't show individual payment info.
        return null;
      }
      var billMessageVecnaIdOrAccountNumber = this._paymentDetailsFormModel.getBillMessageVecnaIdOrAccountNumberFromIdentifier(previouslyPaidBill.identifier);
      var previouslyPaidBillMessage = this._billMessageCollection && this._billMessageCollection.getMessageByVecnaId(billMessageVecnaIdOrAccountNumber);
      var previouslyPaidEstimate = this._insuranceEstimationCollection && this._insuranceEstimationCollection.getByAccountNumber(billMessageVecnaIdOrAccountNumber);
      if (!previouslyPaidBillMessage && !previouslyPaidEstimate) {
        return null;
      }
      var accountNumber = previouslyPaidBillMessage != null ? previouslyPaidBillMessage.get('billDTO.accountNumber') : previouslyPaidEstimate.get('accountNumber');
      var type = previouslyPaidBillMessage != null ? previouslyPaidBillMessage.get('billDTO.externalBill.type') : 'INSURANCE_ESTIMATION';
      return {
        payment: previouslyPaidBill,
        accountNumber: accountNumber,
        type: type
      };
    },
    nextBillToPay: function() {
      if (this._isPaymentInProgress()) {
        return null;
      }
      var nextToBePaidBill = this._paymentDetailsFormModel.getFirstToBePaidBillPaymentDetails();
      if (!nextToBePaidBill) {
        return null;
      }
      var billMessageVecnaIdOrAccountNumber = this._paymentDetailsFormModel.getBillMessageVecnaIdOrAccountNumberFromIdentifier(nextToBePaidBill.identifier);
      var nextPayableBillMessage = this._billMessageCollection && this._billMessageCollection.getMessageByVecnaId(billMessageVecnaIdOrAccountNumber);
      var nextPayableEstimate = this._insuranceEstimationCollection && this._insuranceEstimationCollection.getByAccountNumber(billMessageVecnaIdOrAccountNumber);
      if (!nextPayableBillMessage && !nextPayableEstimate) {
        return null;
      }
      var accountNumber = nextPayableBillMessage != null ? nextPayableBillMessage.get('billDTO.accountNumber') : nextPayableEstimate.get('accountNumber');
      var type = nextPayableBillMessage != null ? nextPayableBillMessage.get('billDTO.externalBill.type') : 'INSURANCE_ESTIMATION';
      return {
        payment: nextToBePaidBill,
        accountNumber: accountNumber,
        type: type
      };
    },
    noStaticContentServer: function() {
      return applicationUtils.currentApplication.noStaticContentServer;
    },
    saveStoredPaymentMethodError: function() {
      if (!this._storedPaymentMethodService) {
        return false;
      }
      var saveStoredPaymentMethodErrors = _.pick(this._storedPaymentMethodService.get('errors'), function(error) {
        return error === true;
      });
      return !_.isEmpty(saveStoredPaymentMethodErrors);
    }
  }),

  injectionSites: {
    'select-payment-method': {
      view: function() {
        // needed because it is shared and injection sites will steal the view if the parent view renders.
        if (this.isActive() && this.isAttached()) {
          return this._selectPaymentMethodView;
        }
      },
      options: { shared: true }
    }
  },

  /**
   * @method initialize
   * @param options {Object}
   *   @param options.patientVecnaId {String} the vecna id of the patient who owns the bills being paid.
   *   @param options.billMessageCollection {Torso.Collection} the collection of payable bill messages.
   *   @param options.paymentDetailsFormModel {PaymentDetailsFormModel} the form model that has all the form data.
   *   @param options.storedPaymentMethodService {StoredPaymentMethodService} the stored payment method service to use.
   *   @param options.selectPaymentMethodView {SelectPaymentMethodView} the view that allows a user to select a payment method.
   * @override
   */
  initialize: function(options) {
    ModalDialogWidget.prototype.initialize.apply(this, arguments);
    _.bindAll(this, 'render', '_handleSuccessfulPayment', '_handleFailedPayment',
                    '_cleanupInProgressState', '_startProcessPayment', '_processPayment', '_submitPayment',
                    '_setPaymentToNewlyCreatedStoredPayment', '_clearSensitiveDataIfNoNextBillToPay',
                    '_copyPatientIdentifiersFromDemographics', '_rejectPromiseIfDemographicsFetchFailed',
                    '_startPollForPaymentToComplete', '_saveSelectedPaymentDevicePreference',
                    '_setDeviceDataOnFormModel', '_handleDeviceErrors', '_saveTransactionVecnaId');

    this._patientVecnaId = options.patientVecnaId;
    this._billMessageCollection = options.billMessageCollection;
    this._insuranceEstimationCollection = options.insuranceEstimationCollection;
    this._paymentDetailsFormModel = options.paymentDetailsFormModel;
    this._storedPaymentMethodService = options.storedPaymentMethodService;
    this.listenTo(this._storedPaymentMethodService, 'change:errors', this.render);

    this._selectPaymentMethodView = options.selectPaymentMethodView;
    this._hideSelectPaymentMethodView();

    this.listenTo(this._paymentDetailsFormModel, 'change:billPaymentDetails', this.render);
    this.on('change:billPaymentsInProgress change:previouslyPaidBill change:paymentDataDeviceError', this.render);
    this.on('submit', this._submit);

    // Using renderIfDisplayed here because it is the only place where render can be called after dispose.
    // In the case of the listeners above they will all be removed on dispose.  This is an issue when 2
    // of these modals are created in quick succession (before the loadOnce() method completes).
    billingConfigModel.loadOnce()
      .then(this._renderIfDisplayed);

    var demographicsDataBehavior = this.getBehavior('demographics');
    var demographicsFetchSuccess = demographicsDataBehavior.get('fetchSuccess');
    if (demographicsFetchSuccess) {
      this._startProcessPayment();
    } else if (demographicsFetchSuccess === false) {
      this._handleFailedPayment();
    } else {
      this.listenToOnce(demographicsDataBehavior, 'fetched', this._startProcessPayment);
    }
  },

  /**
   * Do not close the modal if escape is pressed while a bill payment is being processed.
   * Otherwise the escape key should close the modal.
   * @method keyEscape
   * @override
   */
  keyEscape: function() {
    if (!this._isPaymentInProgress()) {
      // eventually calls this.cancel().
      return ModalDialogWidget.prototype.keyEscape.apply(this, arguments);
    }
  },

  /**
   * Keyboard escape also uses this function.
   * @method cancel
   * @override
   */
  cancel: function() {
    this._paymentDetailsFormModel.clearAllPaymentMethodData();
    var hasBillsToPay = this._paymentDetailsFormModel.hasToBePaidBills();
    if (hasBillsToPay) {
      // close the modal w/o forwarding if it is during the payment process.
      this.rejectModal();
    } else {
      // close the modal and forward to the receipt(s) if the payments are complete.
      this.resolveModal();
    }
  },

  /**
   * @method resolveModal
   * @override
   */
  resolveModal: function() {
    this.unregisterTrackedView(this._selectPaymentMethodView);
    var modalBehavior = this.getBehavior('modalBehavior');
    ModalBehavior.prototype.mixin.resolveModal.apply(modalBehavior, arguments);
  },

  /**
   * @method rejectModal
   * @override
   */
  rejectModal: function() {
    this.unregisterTrackedView(this._selectPaymentMethodView);
    var modalBehavior = this.getBehavior('modalBehavior');
    ModalBehavior.prototype.mixin.rejectModal.apply(modalBehavior, arguments);
  },

  /**
   * Start processing the payment - either one bill or the full collection depending on the configuration.
   * @method _startProcessPayment
   * @private
   */
  _startProcessPayment: function() {
    this.listenTo(this._paymentDetailsFormModel, 'paymentDataReady', this._processPayment);
    this.trigger('submit');
    this._hideSelectPaymentMethodView();
    this._initializeProgressState();
  },

  /**
   * Process the payment - either one bill or the full collection depending on the configuration.
   * @method _processPayment
   * @private
   */
  _processPayment: function() {
    this.stopListening(this._paymentDetailsFormModel, 'paymentDataReady', this._processPayment);
    var isTabletPaymentMethod = this._paymentDetailsFormModel.get('paymentMethod') === this._paymentDetailsFormModel.PAYMENT_METHODS.paymentDataDevice;
    if (isTabletPaymentMethod) {
      return this._makePaymentUsingTabletApp();
    }
    if (this._paymentDetailsFormModel.get('storePayment.save')) {
      return this._createStoredPaymentAndSubmitTransaction();
    } else {
      return this._submitPayment();
    }
  },

  /**
   * Make a payment using the tablet app.
   * @method _makePaymentUsingTabletApp
   * @return {jQuery.Promise} that resolves if the payment is successful and rejects if it fails.
   * @private
   */
  _makePaymentUsingTabletApp: function() {
    return this._preparePaymentData()
      .then(paymentDataDeviceService.startDevicePaymentAndPolling)
      .then(this._saveTransactionVecnaId, this._handleDeviceErrors)
      .then(this._startPollForPaymentToComplete);
  },

  /**
   * Save the transaction vecna id on the current transaction so we can poll for the transaction to complete.
   * @param transactionVecnaId {String} the vecna id of the transaction to poll.
   * @private
   */
  _saveTransactionVecnaId: function(transactionVecnaId) {
    var currentPaymentTransactionModel = this._paymentDetailsFormModel.getCurrentPaymentTransactionModel();
    currentPaymentTransactionModel.set('vecnaId', transactionVecnaId);
  },

  /**
   * @method _setupDemographicsOnPaymentDetailsModel
   * @return {jQuery.Promise} that resolves when the demographics have been added to the payment details model.
   * @private
   */
  _setupDemographicsOnPaymentDetailsModel: function() {
    return this._getDemographicsFetchedPromise()
      .then(this._rejectPromiseIfDemographicsFetchFailed)
      .then(this._copyPatientIdentifiersFromDemographics);
  },

  /**
   * This will create the stored payment on the backend and then make a payment with it.  Once completed (success or failure)
   * it removes the stored payment from the payment details form model.
   * @method _createStoredPaymentAndSubmitTransaction
   * @return {jQuery.Promise} that resolves after the stored payment method has been created and the payment has been submitted.
   * @private
   */
  _createStoredPaymentAndSubmitTransaction: function() {
    return this._setupDemographicsOnPaymentDetailsModel()
      .then(this._storedPaymentMethodService.createStoredPaymentMethod)
      .then(this._setPaymentToNewlyCreatedStoredPayment, _.noop)
      .then(this._submitPayment, this._submitPayment)
      .then(this._paymentDetailsFormModel.removeStorePayment, this._paymentDetailsFormModel.removeStorePayment);
  },

  /**
   * @method _preparePaymentData
   * @return {jQuery.Promise} that resolves after the demographics have been setup on the payment details form model
   *                          and the payment details form model is prepared for sending.
   * @private
   */
  _preparePaymentData: function() {
    return this._setupDemographicsOnPaymentDetailsModel()
      .then(this._paymentDetailsFormModel.preparePaymentData);
  },

  /**
   * Actually submit the payment transaction.
   * @method _submitPayment
   * @private
   */
  _submitPayment: function() {
    return this._preparePaymentData()
      .then(this._paymentDetailsFormModel.saveTransaction)
      .then(this._startPollForPaymentToComplete);
  },

  /**
   * Method to handle payment data returned from paymentDataDeviceService
   * @method _setDeviceDataOnFormModel
   * @param devicePaymentInfo
   * @private
   */
  _setDeviceDataOnFormModel: function(devicePaymentInfo) {
    this._paymentDetailsFormModel.set('tabletDevicePaymentInfo', devicePaymentInfo);
  },

  /**
   * Method to handle errors returned from paymentDataDeviceService
   * @method _handleDeviceErrors
   * @param errors
   * @private
   */
  _handleDeviceErrors: function(errors) {
    this.set('paymentDataDeviceError', errors);
  },

  /**
   * This method attempts to take the event and state based fetch status of demographics data behavior and convert it into
   * a promise that can be used in a promise chain.
   * @method _getDemographicsFetchedPromise
   * @return {jQuery.Promise} a promise that resolves with the fetch result once the demographics have been fetched.
   * @private
   */
  _getDemographicsFetchedPromise: function() {
    var demographicsFetchedDeferred = $.Deferred();

    var demographicsDataBehavior = this.getBehavior('demographics');
    var demographicFetchSuccess = demographicsDataBehavior.get('fetchSuccess');
    if (_.isUndefined(demographicFetchSuccess)) {
      this.listenToOnce(demographicsDataBehavior, 'fetched', function() {
        var fetchSuccess = demographicsDataBehavior.get('fetchSuccess');
        demographicsFetchedDeferred.resolve(fetchSuccess);
      });
    } else {
      demographicsFetchedDeferred.resolve(demographicFetchSuccess);
    }
    return demographicsFetchedDeferred.promise();
  },

  /**
   * @method _rejectPromiseIfDemographicsFetchFailed
   * @param fetchSuccess {Boolean} true if the fetch was successful, false otherwise.
   * @return {Boolean} true if the fetch was successful.  Otherwise it will throw an error.
   * @throws {Error} (which will result in a rejected promise) if the demographics failed to be retrieved successfully.
   * @private
   */
  _rejectPromiseIfDemographicsFetchFailed: function(fetchSuccess) {
    if (fetchSuccess) {
      return fetchSuccess;
    }
    throw new Error('Failed to fetch demographics.');
  },

  /**
   * @method _copyPatientIdentifiersFromDemographics
   * @private
   */
  _copyPatientIdentifiersFromDemographics: function() {
    var demographicsDataBehavior = this.getBehavior('demographics');
    var patientIdentifiers = demographicsDataBehavior.data.get('identifiers');
    this._paymentDetailsFormModel.set('patientIdentifiers', patientIdentifiers);
  },

  /**
   * @method _startPollForPaymentToComplete
   * @return {jQuery.Promise} that resolves when the payment on the backend has completed.
   * @private
   */
  _startPollForPaymentToComplete: function() {
    var paymentPollingPromise = $.Deferred();
    _.bindAll(paymentPollingPromise, 'resolve', 'reject');
    return this._pollForPaymentToComplete(paymentPollingPromise)
      .then(this._handleSuccessfulPayment, this._handleFailedPayment)
      .then(this._saveSelectedPaymentDevicePreference)
      .then(this._clearSensitiveDataIfNoNextBillToPay, this._clearSensitiveDataIfNoNextBillToPay)
      .then(this._cleanupInProgressState, this._cleanupInProgressState);
  },

  /**
   * PaymentTransaction is considered completed if the status is either 'FINISHED' or 'ERRORED'.
   * @method _pollForPaymentToComplete
   * @param [paymentPollingPromise=$.Deferred().promise()] {jQuery.Promise} promise to resolve when polling completes.
   * @return {jQuery.Promise} the promise that will resolve if getting the payment process completes and rejects if it
   *                          fails to retrieve the transaction status.
   * @private
   */
  _pollForPaymentToComplete: function(paymentPollingPromise) {
    if (!paymentPollingPromise) {
      paymentPollingPromise = $.Deferred();
      _.bindAll(paymentPollingPromise, 'resolve', 'reject');
    }
    var newPaymentTransactionModel = this._paymentDetailsFormModel.getCurrentPaymentTransactionModel();
    var thisSubmitPaymentModalView = this;
    newPaymentTransactionModel.fetch()
      .then(function() {
        if (newPaymentTransactionModel.get('status') === PaymentTransactionModel.STATUSES.FINISHED) {
          paymentPollingPromise.resolve.apply(paymentPollingPromise, arguments);
        } else if (newPaymentTransactionModel.get('status') === PaymentTransactionModel.STATUSES.ERRORED) {
          paymentPollingPromise.reject.apply(paymentPollingPromise, arguments);
        } else {
          window.setTimeout(function() {
            thisSubmitPaymentModalView._pollForPaymentToComplete(paymentPollingPromise);
          }, PAYMENT_POLLING_WAIT_MS);
        }
      }, paymentPollingPromise.reject);
    return paymentPollingPromise.promise();
  },

  /**
   * Select a new payment method - clear existing payment method entirely and allow user to select a payment.
   * @method _selectNewPaymentMethod
   * @private
   */
  _selectNewPaymentMethod: function() {
    this._paymentDetailsFormModel.clearAllPaymentMethodData();
    this._paymentDetailsFormModel.unset('paymentMethod');
    this._selectPaymentMethodView._setPaymentDefaults();
    this._showSelectPaymentMethodView();
  },

  /**
   * Show the select payment method and change the modal to a full-sized modal.
   * @method _showSelectPaymentMethodView
   * @private
   */
  _showSelectPaymentMethodView: function() {
    this.setClassName('brec-dialog');
    this.set('hide:select-payment-method', false);
  },

  /**
   * Hide the select payment method and change the modal back to a small modal.
   * @method _hideSelectPaymentMethodView
   * @private
   */
  _hideSelectPaymentMethodView: function() {
    this.set('hide:select-payment-method', true);
    this.setClassName('brec-dialog brec-dialog--small');
  },

  /**
   * Trigger any in-page validation on any sub-views of this view.
   * @method _submit
   * @private
   */
  _submit: function() {
    this._selectPaymentMethodView.trigger('submit');
  },

  /**
   * Set the payment method to the newly created stored payment method.
   * @method _setPaymentToNewlyCreatedStoredPayment
   * @private
   */
  _setPaymentToNewlyCreatedStoredPayment: function() {
    this._paymentDetailsFormModel.set('paymentMethod', this._paymentDetailsFormModel.get('storePayment.vecnaId'));
  },

  /**
   * Initialize the state of the view so that no payments are in progress.
   * @method _initializeProgressState
   * @private
   */
  _initializeProgressState: function() {
    // Make sure any race conditions are handled by setting state immediately.
    this.unset('previouslyPaidBill');
    this.unset('multipleBillsPaidSuccess');
    this.unset('submitPaymentFailed');
    this.unset('paymentDataDeviceError');

    var paymentDetailsFormModel = this._paymentDetailsFormModel;
    var currentBillPaymentDetails = paymentDetailsFormModel.getCurrentBillPaymentDetails();
    this.set('billPaymentsInProgress', currentBillPaymentDetails);
  },

  /**
   * Handle a successful payment response.
   * @param modelPollResponse {Object} - not used
   * @param modelPollStatus {String} - either 'success' if successful or something else if not successful.
   * @return {Object} - the result of the save of the transaction data model.
   * @private
   */
  _handleSuccessfulPayment: function(modelPollResponse, modelPollStatus) {
    var newPaymentTransactionModel = this._paymentDetailsFormModel.getCurrentPaymentTransactionModel();

    if (modelPollStatus === 'success') {
      var isEreg = applicationUtils.currentApplication === applicationUtils.APPLICATIONS.EREG;
      var isPatientPortal = applicationUtils.currentApplication === applicationUtils.APPLICATIONS.PATIENT_PORTAL;
      if (isEreg || isPatientPortal) {
        this._createVisitPayment(newPaymentTransactionModel);
      }

      this._paymentDetailsFormModel.markBillsPaid(newPaymentTransactionModel);
      var billsJustPaid = this.get('billPaymentsInProgress');
      if (billsJustPaid && billsJustPaid.length > 1) {
        this.set('multipleBillsPaidSuccess', true);
      } else {
        var billJustPaid = billsJustPaid[0];
        billJustPaid.receiptVecnaId = newPaymentTransactionModel.get('receiptVecnaId');
        this.set('previouslyPaidBill', billsJustPaid);
      }
      this._refetchPaidBillMessage(billsJustPaid);
    } else {
      return this._handleFailedPayment.apply(this, arguments);
    }
    return modelPollResponse;
  },

  /**
   * Handle saving the device use to the user.
   * @method _saveSelectedPaymentDevicePreference
   * @param modelPollResponse {Object} - not used
   * @return {Object} - the result of the save of the transaction data model.
   * @private
   */
  _saveSelectedPaymentDevicePreference: function(modelPollResponse) {
    var paymentDeviceId = modelPollResponse && modelPollResponse.request && modelPollResponse.request.paymentDeviceId;
    if (!paymentDeviceId) {
      return modelPollResponse;
    }

    var adminUserModel = userService.currentAdminUserModel;
    if (!adminUserModel) {
      return modelPollResponse;
    }

    var returnModelPollResponseFn = function() {
      return modelPollResponse;
    };

    adminUserModel.set('preferredDeviceId', paymentDeviceId);
    return adminUserModel.save()
      .then(returnModelPollResponseFn, returnModelPollResponseFn);
  },

  _createVisitPayment: function(paymentTransactionModel) {
    $.ajax({
      url: '/patient-portal/rest/secure/payment/process/visitPayment',
      type: 'POST',
      data: JSON.stringify(paymentTransactionModel),
      contentType: 'application/json'
    }).always();
  },

  /**
   * Refetch paid bill messages
   * @param paidBills
   * @private
   */
  _refetchPaidBillMessage: function(paidBills) {
    var view = this;
    var identifiers = _.pluck(paidBills, 'identifier');
    var vecnaIds = _.map(identifiers, function(identifier) {
      view._paymentDetailsFormModel.getBillMessageVecnaIdOrAccountNumberFromIdentifier(identifier);
    });
    var billMessageCollection = this._billMessageCollection;
    var paidBillMessages = billMessageCollection.filter(function(model) {
          return _.contains(vecnaIds, model.get('vecnaId'));
        });
    var ids = _.pluck(paidBillMessages, 'id');
    billMessageCollection.fetchByIds(ids);
  },

  /**
   * Handle a failed payment response.
   * @method _handleFailedPayment
   * @param formModelSaveResponse
   * @return {Object} - the result of the save of the transaction data model.
   * @private
   */
  _handleFailedPayment: function(formModelSaveResponse) {
    var formModelSaveResponses = formModelSaveResponse;
    if (!_.isArray(formModelSaveResponses)) {
      formModelSaveResponses = [ formModelSaveResponses ];
    }
    var saveStatuses = _.pluck(formModelSaveResponses, 'status');
    var saveFailed = _.contains(saveStatuses, PaymentTransactionModel.STATUSES.ERRORED);
    // Handles 404, 400, 500, etc.
    var saveErrored = !_.every(saveStatuses, SUCCESS_STATUS_CODE_200);
    this.set('submitPaymentFailed', saveFailed || saveErrored);
  },

  /**
   * Cleanup the view state tracking bill progress.
   * @method _cleanupInProgressState
   * @private
   */
  _cleanupInProgressState: function() {
    this.unset('billPaymentsInProgress');
  },

  /**
   * Clear any sensitive data card or ach data from the form model if no next bill to pay.
   * @method _clearSensitiveDataIfNoNextBillToPay
   * @private
   */
  _clearSensitiveDataIfNoNextBillToPay: function() {
    var nextToBePaidBill = this._paymentDetailsFormModel.getFirstToBePaidBillPaymentDetails();
    if (!nextToBePaidBill) {
      this._paymentDetailsFormModel.clearSensitivePaymentData();
    }
  },

  /**
   * @method _isPaymentInProgress
   * @return {Boolean} true if there is any type of payment in progress, false otherwise.
   * @private
   */
  _isPaymentInProgress: function() {
    var billPaymentsInProgress = this.get('billPaymentsInProgress');
    return billPaymentsInProgress && !_.isEmpty(billPaymentsInProgress);
  }
});
